// "View QA Priority Queue" popup on Lead QA Review: the WIP programs in the order the QA team should work
// them, with the counts of Pending Review, Under Review, Approved Pending Call Review and DemandArc Accepted
// leads. Ranked automatically by the leads still waiting; a Program Manager or Admin can move programs up or
// down (shared with everyone, timestamped, with who set it) and reset to automatic; it resets itself each week.
// Counts are links into that program's Lead QA Review tab. Backend: /qa-priority. Pure helpers: utils/qaPriority.js.
//
// Props: onClose, onJump({ programId, status }) — status is a Lead QA Review tab or 'all',
//        canOverride — show the move arrows and the reset button.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { LEAD_STATUS_COLORS } from '../utils/leadStatus';
import { QUEUE_COLUMNS, endOfWeekLabel, formatSetAt, jumpTarget, movedOrder } from '../utils/qaPriority';

const API = process.env.REACT_APP_API_URL;

const th = { textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#555', padding: '8px 10px', background: '#f5f6f9', borderBottom: '1px solid #e3e7ee', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', borderBottom: '1px solid #f0f2f4', verticalAlign: 'middle', fontSize: '13px' };
const arrow = (disabled) => ({
  border: '1px solid #ccd4dd', background: disabled ? '#f5f6f9' : 'white', color: disabled ? '#bbb' : '#2c3e50',
  borderRadius: '4px', width: '24px', height: '22px', cursor: disabled ? 'default' : 'pointer', fontSize: '11px', padding: 0,
});

const errorText = (e) => (e?.response?.data?.detail && typeof e.response.data.detail === 'string')
  ? e.response.data.detail : (e?.message || 'Something went wrong');

function CountCell({ program, column, onJump }) {
  const n = program[column.key];
  const colors = LEAD_STATUS_COLORS[column.status] || {};
  if (!n) return <span style={{ color: '#bbb' }}>—</span>;
  const pill = { background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`, borderRadius: '4px', padding: '2px 9px', fontWeight: 700, fontSize: '12px' };
  if (!program.can_open) {
    return <span style={pill} title="Not one of your programs">{n.toLocaleString()}</span>;
  }
  return (
    <button type="button" onClick={() => onJump(jumpTarget(program, column.key))}
      title={`Open ${program.program_name} in Lead QA Review`}
      style={{ ...pill, cursor: 'pointer', textDecoration: 'underline' }}>
      {n.toLocaleString()}
    </button>
  );
}

export default function QAPriorityQueue({ onClose, onJump, canOverride }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/qa-priority/`).then(r => setData(r.data)).catch(e => setError(errorText(e)));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useCallback(async (request) => {
    setBusy(true); setError('');
    try { setData((await request()).data); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }, []);

  const move = (programId, direction) => {
    const ids = movedOrder(data.programs, programId, direction);
    if (ids) save(() => axios.put(`${API}/qa-priority/order`, { program_ids: ids }));
  };
  const reset = () => save(() => axios.delete(`${API}/qa-priority/order`));

  const manual = data?.mode === 'manual';
  return (
    <div role="dialog" aria-modal="true" aria-label="QA Priority Queue" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(19,24,67,0.55)', zIndex: 1500, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '28px 16px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: '980px', borderRadius: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '1px solid #e3e7ee', background: '#f8f9fa', borderRadius: '8px 8px 0 0' }}>
          <strong style={{ color: '#2c3e50', fontSize: '15px' }}>QA Priority Queue</strong>
          <button type="button" onClick={onClose}
            style={{ padding: '5px 14px', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Close</button>
        </div>

        <div style={{ padding: '14px 18px' }}>
          {error && <div style={{ background: '#fdecea', border: '1px solid #e6b0aa', color: '#922b21', padding: '8px 12px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>{error}</div>}
          {!data && !error && <div style={{ color: '#757575' }}>Loading the queue…</div>}

          {data && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', background: manual ? '#fdf2e0' : '#eef2f6', border: `1px solid ${manual ? '#f0d9a8' : '#dde3ea'}`, borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: manual ? '#8a5a0b' : '#475569' }}>
                <span>
                  {manual
                    ? <><strong>Manual order</strong> set by {data.override.set_by_name || 'a team member'} on {formatSetAt(data.override.set_at)}. Goes back to automatic at the end of the week ({endOfWeekLabel(data.resets_on)}).</>
                    : <><strong>Automatic order:</strong> the most leads waiting to be processed (Pending Review + Under Review + Call Review) first, then programs holding DemandArc Accepted leads, then the rest.{canOverride ? ' Use the arrows to set your own order.' : ''}</>}
                </span>
                {canOverride && manual && (
                  <button type="button" onClick={reset} disabled={busy}
                    style={{ padding: '4px 12px', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '4px', cursor: busy ? 'default' : 'pointer', fontSize: '12px', fontWeight: 600 }}>
                    Reset to auto
                  </button>
                )}
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid #e3e7ee', borderRadius: '6px' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: '40px' }}>#</th>
                      <th style={th}>Program</th>
                      {QUEUE_COLUMNS.map(c => <th key={c.key} style={{ ...th, textAlign: 'center' }}>{c.label}</th>)}
                      {canOverride && <th style={{ ...th, width: '64px', textAlign: 'center' }}>Move</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.programs.length === 0 && (
                      <tr><td style={{ ...td, color: '#757575' }} colSpan={QUEUE_COLUMNS.length + 3}>No WIP programs right now.</td></tr>
                    )}
                    {data.programs.map((p, i) => (
                      <tr key={p.program_id}>
                        <td style={{ ...td, color: '#757575', fontWeight: 700 }}>{p.rank}</td>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: '#2c3e50' }}>{p.program_name}</div>
                          <div style={{ fontSize: '11px', color: '#757575' }}>{p.client_name}{manual && p.auto_rank !== p.rank ? ` · auto rank ${p.auto_rank}` : ''}</div>
                        </td>
                        {QUEUE_COLUMNS.map(c => (
                          <td key={c.key} style={{ ...td, textAlign: 'center' }}><CountCell program={p} column={c} onJump={onJump} /></td>
                        ))}
                        {canOverride && (
                          <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button type="button" aria-label={`Move ${p.program_name} up`} disabled={busy || i === 0} style={arrow(busy || i === 0)} onClick={() => move(p.program_id, 'up')}>▲</button>{' '}
                            <button type="button" aria-label={`Move ${p.program_name} down`} disabled={busy || i === data.programs.length - 1} style={arrow(busy || i === data.programs.length - 1)} onClick={() => move(p.program_id, 'down')}>▼</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '11px', color: '#757575', marginTop: '8px' }}>
                Click a count to open that program in Lead QA Review. Pending Review opens the program's main view, where incoming pending deliveries can be accepted and reviewed. Counts for programs that aren't yours are shown without a link.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
